import {
  Users, CreditCard, FileText,
  CalendarDays, ClipboardCheck, BookOpen, GraduationCap,
  BarChart3, Globe
} from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Student & Parent Management",
    desc: "Full student profiles, admission records, parent contacts, and academic history. Parents get their own portal to track their child's progress in real-time.",
  },
  {
    icon: CreditCard,
    title: "Fee & Accounts",
    desc: "Automated invoicing, payment tracking, expense management, income reports, and complete financial oversight. See who has paid and who hasn't — instantly.",
  },
  {
    icon: FileText,
    title: "Exams & Report Cards",
    desc: "Create exams, schedule them, manage grading, generate mark sheets, progress cards, and terminal reports — all aligned with GES standards.",
  },
  {
    icon: ClipboardCheck,
    title: "Attendance System",
    desc: "Track student, teacher, and staff attendance daily. Automated reports and instant parent notifications when a child is absent.",
  },
  {
    icon: CalendarDays,
    title: "Timetable & Routine",
    desc: "Smart class scheduling that prevents conflicts. Generate and share timetables for every class, section, and teacher automatically.",
  },
  {
    icon: GraduationCap,
    title: "Teacher & HR Portal",
    desc: "Manage teacher profiles, payroll, salary templates, leave applications, and performance tracking. Everything HR needs in one place.",
  },
  {
    icon: BookOpen,
    title: "Library & E-Books",
    desc: "Catalog books, manage members, track issue/returns, handle fines, and provide access to digital e-books — all from the system.",
  },
  {
    icon: BarChart3,
    title: "Reports & Analytics",
    desc: "50+ built-in reports — class reports, attendance, fees, mark sheets, tabulation sheets, sponsorship, and more. Export to PDF or print instantly.",
  },
  {
    icon: Globe,
    title: "School Website & Admissions",
    desc: "Built-in school website with online admissions, event management, notice board, and content pages. Your school's online presence — included.",
  },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="relative bg-white py-24 lg:py-32 overflow-hidden">
      <div className="blob-amber" style={{ top: '10%', right: '-10%' }} />
      <div className="blob-violet" style={{ bottom: '5%', left: '-8%' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0A0A0A] mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="features-heading"
          >
            Everything You Need
          </h2>
          <p
            className="text-base text-gray-500 max-w-xl mx-auto font-medium"
            style={{ fontFamily: "'Satoshi', sans-serif" }}
          >
            A complete cloud-based school management system with every module your school will ever need.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                data-testid={`feature-card-${i}`}
                className={`hover-lift bg-gray-50 border-2 border-gray-100 p-8 group cursor-default ${
                  i === 9 ? "md:col-span-2 lg:col-span-1" : ""
                }`}
              >
                <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center mb-5 group-hover:bg-[#1a1a1a] transition-colors">
                  <Icon size={22} className="text-white" strokeWidth={2.5} />
                </div>
                <h3
                  className="text-lg font-black text-[#0A0A0A] mb-2 tracking-tight uppercase"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {feature.title}
                </h3>
                <p
                  className="text-sm text-gray-500 leading-relaxed font-medium"
                  style={{ fontFamily: "'Satoshi', sans-serif" }}
                >
                  {feature.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
